// client/src/components/layout/AppRoutes.tsx
//
// The app's route table, lifted out of App.tsx so it can be rendered either once
// (untabbed shell) or once per tab inside the tab host. Nothing here knows about
// tabs — each copy simply renders against whichever router encloses it.
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '../auth/ProtectedRoute';
import Login from '../auth/Login';
import Unauthorized from '../auth/Unauthorized';
import ForgotPassword from '../auth/ForgotPassword';
import SetPassword from '../auth/SetPassword';
import OperationOverview from '../pages/OperationOverview';
import SignaturesRequiredJs from '../pages/SignaturesRequired';
import AmendmentsAllocateTransactions from '../pages/AmendmentsAllocateTransactions';
import AmendmentApprovals from '../pages/AmendmentApprovals';
import AmendmentAnalytics from '../pages/AmendmentAnalytics';
import PDFGeneratorV2 from '../pages/PDFGeneratorV2';
import DemoModeSettings from '../pages/Settings/DemoModeSettings';
import SettingsHome from '../pages/Settings/SettingsHome';
import LogoSettings from '../pages/Settings/LogoSettings';
import ThemeSettings from '../pages/Settings/ThemeSettings';
import EmailSettings from '../pages/Settings/EmailSettings';
import NotificationSettings from '../pages/Settings/NotificationSettings';
import DatabaseSettings from '../pages/Settings/DatabaseSettings';
import OrderManagementSettings from '../pages/Settings/OrderManagementSettings';
import AccountSetup from '../pages/Settings/AccountSetup';
import ComingSoonJs from '../pages/Settings/ComingSoon';
import TenantDetails from '../pages/Settings/TenantDetails';
import UserManagement from '../pages/Settings/UserManagement';
import RoleManagement from '../pages/Settings/RoleManagement';
import SubscriptionUsage from '../pages/Settings/SubscriptionUsage';
import TenantManagement from '../pages/Settings/TenantManagement';
import TelemetryDevices from '../pages/Settings/TelemetryDevices';
import LexiStarAssets from '../pages/Settings/LexiStarAssets/LexiStarAssets';
import LexiStarWebhooks from '../pages/Settings/LexiStarWebhooks/LexiStarWebhooks'; // ST-00207 M4b-B
import LexiStarDataExport from '../pages/Settings/LexiStarDataExport/LexiStarDataExport'; // ST-00207 M4b-C
import LexiStarRoutesLanes from '../pages/LexiStar/Routes/RoutesLanes';
import LexiStarFuelConfig from '../pages/LexiStar/FuelConfig/FuelConfig';
import ApiKeys from '../pages/Settings/ApiKeys';
import RouteSummary from '../pages/RouteSummary';
import LocationAnalytics from '../pages/LocationAnalytics';
import VehicleRouteTracker from '../pages/VehicleRouteTracker';
import TransactionAllocation from '../pages/TransactionAllocation';
import EmailReports from '../pages/EmailReports';
import ErrorDetection from '../pages/ErrorDetection';
import RouteIntelligence from '../pages/RouteIntelligence';
import ProductMovement from '../pages/ProductMovement';
import PeriodSummary from '../pages/PeriodSummary';
import CustomSummaries from '../pages/CustomSummaries';
// Import Telegram BI components
import TelegramDashboard from '../pages/TelegramBI/Dashboard';
import TelegramQueryBuilder from '../pages/TelegramBI/QueryBuilder';
import TelegramTemplateManager from '../pages/TelegramBI/TemplateManager';
import TelegramScheduleManager from '../pages/TelegramBI/ScheduleManager';
import TelegramAnalytics from '../pages/TelegramBI/Analytics';
import TelegramBotManager from '../pages/TelegramBI/BotManager';
// Import Order Management components
import {
  OrderDashboard,
  OrderList,
  CreateOrder,
  OrderDetails,
  Products,
  Locations,
  Companies,
  Vehicles,
  Drivers,
  BulkUpload,
  HaulierAllocation,
} from '../pages/OrderManagement';
import SyncDashboard from '../pages/SyncDashboard';
import Geofencing from '../pages/Geofencing';
import LexiStarLiveView from '../pages/LexiStar/LiveView/LiveView'; // ST-00210
import LexiStarStatusBoard from '../pages/LexiStar/StatusBoard/StatusBoard';
// LexiStar maintenance and the time model (ST-00249)
import LexiStarMaintenance from '../pages/LexiStar/Maintenance/Maintenance';
import LexiStarMaintenanceCodes from '../pages/Settings/LexiStarMaintenanceCodes/MaintenanceCodes';
import LexiStarDisplayRules from '../pages/Settings/LexiStarDisplayRules/DisplayRulesSettings';
import LexiStarApprovals from '../pages/LexiStar/Approvals/Approvals';
import LexiStarTripDetail from '../pages/LexiStar/TripDetail/TripDetail';
// LexiStar assurance (ST-00188 M3-U1)
import LexiStarReviewQueue from '../pages/LexiStar/ReviewQueue/ReviewQueue';
import LexiStarRecomputeJobs from '../pages/LexiStar/RecomputeJobs/RecomputeJobs';
import LexiStarCommissioning from '../pages/LexiStar/Commissioning/Commissioning';
import LexiStarCertificate from '../pages/LexiStar/Commissioning/Certificate';
import LexiStarDeclarations from '../pages/LexiStar/Declarations/Declarations';
import LexiStarPlanDispatch from '../pages/LexiStar/PlanDispatch/PlanDispatch';
// LexiStar dispatch planning board (ST-00250)
import LexiStarDispatchBoard from '../pages/LexiStar/DispatchBoard/DispatchBoard';
// LexiStar operations (ST-00188 M3-U2)
import LexiStarPlanBoard from '../pages/LexiStar/PlanBoard/PlanBoard';
import LexiStarReplay from '../pages/LexiStar/Replay/Replay';
import LexiStarDrivers from '../pages/LexiStar/Drivers/Drivers';
import LexiStarMessages from '../pages/LexiStar/Messages/Messages';
// LexiStar notifications inbox (ST-00251)
import LexiStarNotificationsInbox from '../pages/LexiStar/Notifications/NotificationsInbox';
import LexiStarNotificationRules from '../pages/LexiStar/NotificationRules/NotificationRules';
// LexiStar reports (ST-00188 M4-R2) — TSX pages
import LexiStarReportsCentre from '../pages/LexiStar/Reports/ReportsCentre';
import LexiStarTripReport from '../pages/LexiStar/Reports/TripReport';
import LexiStarRunReport from '../pages/LexiStar/Reports/RunReport';
import LexiStarDailySummary from '../pages/LexiStar/Reports/DailySummary';
import LexiStarStandardReport from '../pages/LexiStar/Reports/StandardReport';
// LexiStar numbers (ST-00188 M4-D)
import LexiStarDashboard from '../pages/LexiStar/Dashboard/Dashboard';
import LexiStarKpis from '../pages/LexiStar/Kpis/Kpis';
import LexiStarRouteTruckDriver from '../pages/LexiStar/RouteTruckDriver/RouteTruckDriver';
import LexiStarDwell from '../pages/LexiStar/Dwell/Dwell';
import LexiStarFleetTargets from '../pages/LexiStar/FleetTargets/FleetTargets'; // ST-00247 / ST-00248
import LexiStarTripsDrill from '../pages/LexiStar/Dashboard/TripsDrill';
// LexiStar usage & billing (ST-00207 M4b-A)
import LexiStarBilling from '../pages/LexiStar/Billing/Billing';
import LexiStarStatementPrint from '../pages/LexiStar/Billing/StatementPrint';
import DriverPWA from '../pages/DriverPWA';
import WeighbridgeCompliance from '../pages/WeighbridgeCompliance';
import TransactionSearch from '../pages/TransactionSearch';
import LoadingSpinnerJs from '../common/LoadingSpinner';

// Help & training (ST-00200): lazy, so react-markdown and the guide texts load only when a
// Help page is opened. Any signed-in user (config/pagePermissions '/help': []).
const HelpHome = lazy(() => import('../pages/Help/HelpHome'));
const HelpTraining = lazy(() => import('../pages/Help/HelpTraining'));
const HelpGlossary = lazy(() => import('../pages/Help/HelpGlossary'));
const HelpSectionPage = lazy(() => import('../pages/Help/HelpSectionPage'));
const HelpArticlePage = lazy(() => import('../pages/Help/HelpArticlePage'));

/** The signatures page's route (from before the tab shell): a dispatch → receipt pair. */
export interface SharedRoute {
  dispatchLocation?: string;
  receiptLocation?: string;
}

/** Page state that predates tabs and is still shared app-wide (App.tsx routePropsOf). */
export interface AppRoutesProps {
  selectedRoute: SharedRoute | null;
  setSelectedRoute: (route: SharedRoute | null) => void;
  selectedMonth: number;
  setSelectedMonth: (month: number) => void;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
}

// Typed views of the JavaScript components whose props TypeScript would
// otherwise infer from their destructuring (every binding read as required).
// Still handed the shared route / month / year it once used (it now reads none of it).
const SignaturesRequired = SignaturesRequiredJs as unknown as ComponentType<AppRoutesProps>;
const LoadingSpinner = LoadingSpinnerJs as unknown as ComponentType<{
  size?: 'sm' | 'md' | 'lg' | 'xl';
}>;

/** A lazily loaded page behind ProtectedRoute, with a spinner while its chunk loads. */
function LazyPage({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <Suspense
        fallback={
          <div className="flex justify-center py-16" role="status" aria-label="Loading">
            <LoadingSpinner size="lg" />
          </div>
        }
      >
        {children}
      </Suspense>
    </ProtectedRoute>
  );
}

const ComingSoon = ComingSoonJs as unknown as ComponentType<{
  title: string;
  description: string;
  breadcrumbItems: { label: string; path?: string }[];
  icon?: ComponentType;
}>;

export default function AppRoutes({
  selectedRoute,
  setSelectedRoute,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
}: AppRoutesProps) {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/set-password" element={<SetPassword />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <OperationOverview />
          </ProtectedRoute>
        }
      />

      <Route
        path="/signatures"
        element={
          <ProtectedRoute>
            <SignaturesRequired
              selectedRoute={selectedRoute}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              setSelectedMonth={setSelectedMonth}
              setSelectedYear={setSelectedYear}
              setSelectedRoute={setSelectedRoute}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/amendments-allocate-transactions"
        element={
          <ProtectedRoute>
            <AmendmentsAllocateTransactions />
          </ProtectedRoute>
        }
      />

      <Route
        path="/transaction-search"
        element={
          <ProtectedRoute>
            <TransactionSearch />
          </ProtectedRoute>
        }
      />

      <Route
        path="/amendment-approvals"
        element={
          <ProtectedRoute>
            <AmendmentApprovals />
          </ProtectedRoute>
        }
      />

      <Route
        path="/amendment-analytics"
        element={
          <ProtectedRoute>
            <AmendmentAnalytics />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pdf-generator-v2"
        element={
          <ProtectedRoute>
            <PDFGeneratorV2 />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsHome />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/branding/logo"
        element={
          <ProtectedRoute>
            <LogoSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/branding/theme"
        element={
          <ProtectedRoute>
            <ThemeSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/communications/email"
        element={
          <ProtectedRoute>
            <EmailSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/communications/signatures"
        element={
          <ProtectedRoute>
            <Navigate to="/signatures" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/documents/pdf-templates"
        element={
          <ProtectedRoute>
            <Navigate to="/pdf-generator-v2" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/users/manage"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/users/roles"
        element={
          <ProtectedRoute>
            <RoleManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/integrations/api-keys"
        element={
          <ProtectedRoute>
            <ApiKeys />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/account-setup/tenant"
        element={
          <ProtectedRoute>
            <TenantDetails />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/account-setup/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionUsage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/account-setup/route-groupings"
        element={
          <ProtectedRoute>
            <AccountSetup />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organization/account-setup/tenant-management"
        element={
          // Global admins only: every API on this page answers 403 to anyone else (ST-00222).
          <ProtectedRoute roles={['global-admin']}>
            <TenantManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/reporting/custom-summaries"
        element={
          <ProtectedRoute>
            <Navigate to="/custom-summaries" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/reporting/email-reports"
        element={
          <ProtectedRoute>
            <Navigate to="/email-reports" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/reporting/telegram-bi"
        element={
          <ProtectedRoute>
            <Navigate to="/telegram-bi" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/operations/amendments"
        element={
          <ProtectedRoute>
            <Navigate to="/amendments-allocate-transactions" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/operations/transaction-allocation"
        element={
          <ProtectedRoute>
            <ComingSoon
              title="Transaction Allocation"
              description="Manage transaction allocation rules"
              breadcrumbItems={[
                { label: 'Modules' },
                { label: 'Operations' },
                { label: 'Transaction Allocation' },
              ]}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/monitoring/error-detection"
        element={
          <ProtectedRoute>
            <Navigate to="/error-detection" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/monitoring/geofencing"
        element={
          <ProtectedRoute>
            <Navigate to="/geofencing" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/monitoring/telemetry-devices"
        element={
          <ProtectedRoute>
            <TelemetryDevices />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/monitoring/lexistar-assets"
        element={
          <ProtectedRoute>
            <LexiStarAssets />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/monitoring/lexistar-routes"
        element={
          <ProtectedRoute>
            <LexiStarRoutesLanes />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/monitoring/lexistar-fuel"
        element={
          <ProtectedRoute>
            <LexiStarFuelConfig />
          </ProtectedRoute>
        }
      />

      {/* LexiStar notification rules, groups, templates, budgets (ST-00188 M3-U2) */}
      <Route
        path="/settings/modules/monitoring/lexistar-notifications"
        element={
          <ProtectedRoute>
            <LexiStarNotificationRules />
          </ProtectedRoute>
        }
      />

      {/* LexiStar signed outbound webhooks (ST-00207 M4b-B) — lexistar.integrations.view in config/pagePermissions */}
      <Route
        path="/settings/modules/monitoring/lexistar-webhooks"
        element={
          <ProtectedRoute>
            <LexiStarWebhooks />
          </ProtectedRoute>
        }
      />

      {/* LexiStar per-tenant data export (ST-00207 M4b-C) — lexistar.export.manage in config/pagePermissions */}
      <Route
        path="/settings/modules/monitoring/lexistar-export"
        element={
          <ProtectedRoute>
            <LexiStarDataExport />
          </ProtectedRoute>
        }
      />

      {/* Settings → LexiStar → Maintenance & delay codes (ST-00249) — lexistar.maintenance.view | .manage */}
      <Route
        path="/settings/modules/lexistar/maintenance-codes"
        element={
          <ProtectedRoute>
            <LexiStarMaintenanceCodes />
          </ProtectedRoute>
        }
      />

      {/* Settings → LexiStar → Map colours & states (ST-00276) — lexistar.trips.view | .notifications.manage */}
      <Route
        path="/settings/modules/lexistar/map-colours"
        element={
          <ProtectedRoute>
            <LexiStarDisplayRules />
          </ProtectedRoute>
        }
      />

      {/* Settings → LexiStar → Usage & billing (ST-00240): the hub's way to the billing page,
          which stays at /lexistar/billing (report definition usage_statement prints its
          /statement/:month page). lexistar.billing.view in config/pagePermissions. */}
      {/* Settings → LexiStar → Performance targets (ST-00248): the hub's way to the Targets tab
          of Fleet & targets. lexistar.kpis.view in config/pagePermissions. */}
      <Route
        path="/settings/modules/lexistar/targets"
        element={
          <ProtectedRoute>
            <Navigate to="/lexistar/fleet-performance?tab=targets" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/modules/lexistar/usage-billing"
        element={
          <ProtectedRoute>
            <Navigate to="/lexistar/billing" replace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/database/performance"
        element={
          // Global admins only: every API on this page answers 403 to anyone else (ST-00222).
          <ProtectedRoute roles={['global-admin']}>
            <DatabaseSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/presentation/demo-mode"
        element={
          <ProtectedRoute>
            <DemoModeSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/modules/order-management/master-data"
        element={
          <ProtectedRoute>
            <OrderManagementSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/route-summary"
        element={
          <ProtectedRoute>
            <RouteSummary />
          </ProtectedRoute>
        }
      />

      <Route
        path="/location-analytics"
        element={
          <ProtectedRoute>
            <LocationAnalytics />
          </ProtectedRoute>
        }
      />

      <Route
        path="/vehicle-tracker"
        element={
          <ProtectedRoute>
            <VehicleRouteTracker />
          </ProtectedRoute>
        }
      />

      <Route
        path="/custom-summaries"
        element={
          <ProtectedRoute>
            <CustomSummaries />
          </ProtectedRoute>
        }
      />

      <Route
        path="/transaction-allocation"
        element={
          <ProtectedRoute>
            <TransactionAllocation />
          </ProtectedRoute>
        }
      />

      <Route
        path="/email-reports"
        element={
          <ProtectedRoute>
            <EmailReports />
          </ProtectedRoute>
        }
      />

      <Route
        path="/error-detection"
        element={
          <ProtectedRoute>
            <ErrorDetection />
          </ProtectedRoute>
        }
      />

      <Route
        path="/route-intelligence"
        element={
          <ProtectedRoute>
            <RouteIntelligence />
          </ProtectedRoute>
        }
      />

      <Route
        path="/product-movement"
        element={
          <ProtectedRoute>
            <ProductMovement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/period-summary"
        element={
          <ProtectedRoute>
            <PeriodSummary />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management"
        element={
          <ProtectedRoute>
            <OrderDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/orders"
        element={
          <ProtectedRoute>
            <OrderList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/orders/create"
        element={
          <ProtectedRoute>
            <CreateOrder />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/orders/:orderNo"
        element={
          <ProtectedRoute>
            <OrderDetails />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/products"
        element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/locations"
        element={
          <ProtectedRoute>
            <Locations />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/companies"
        element={
          <ProtectedRoute>
            <Companies />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/vehicles"
        element={
          <ProtectedRoute>
            <Vehicles />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/drivers"
        element={
          <ProtectedRoute>
            <Drivers />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/bulk-upload"
        element={
          <ProtectedRoute>
            <BulkUpload />
          </ProtectedRoute>
        }
      />

      <Route
        path="/order-management/allocate"
        element={
          <ProtectedRoute>
            <HaulierAllocation />
          </ProtectedRoute>
        }
      />

      {/* Geofences: Zones, Events, Analytics, Aerials (?tab=). The old Live Map tab's
          address, ?tab=map / ?tab=live, goes to Live View (ST-00210); the old Trips tab's,
          ?tab=trips…, to Replay's Trips tab with its view (ST-00318). */}
      <Route
        path="/geofencing"
        element={
          <ProtectedRoute>
            <Geofencing />
          </ProtectedRoute>
        }
      />

      {/* Live View (ST-00210): the live map, out of Geofences and into LexiStar · Operate.
          geofencing.view, as the map always needed (config/pagePermissions). */}
      <Route
        path="/lexistar/live"
        element={
          <ProtectedRoute>
            <LexiStarLiveView />
          </ProtectedRoute>
        }
      />

      {/* LexiStar is the module's product name. /lexistar opened the live map (via /geofencing)
          before ST-00210, so it opens Live View. */}
      <Route path="/lexistar" element={<Navigate to="/lexistar/live" replace />} />

      {/* LexiStar numbers (ST-00188 M4-D) — permissions in config/pagePermissions. /lexistar/trips
          is the drill-through list behind every KPI, and since ST-00318 the sidebar's Trip list
          (the detail stays /lexistar/trips/:id). */}
      <Route
        path="/lexistar/dashboard"
        element={
          <ProtectedRoute>
            <LexiStarDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/kpis"
        element={
          <ProtectedRoute>
            <LexiStarKpis />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/routes-trucks-drivers"
        element={
          <ProtectedRoute>
            <LexiStarRouteTruckDriver />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/dwell"
        element={
          <ProtectedRoute>
            <LexiStarDwell />
          </ProtectedRoute>
        }
      />
      {/* Fleet & targets (ST-00247 availability and utilisation, ST-00248 targets) — lexistar.kpis.view */}
      <Route
        path="/lexistar/fleet-performance"
        element={
          <ProtectedRoute>
            <LexiStarFleetTargets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/trips"
        element={
          <ProtectedRoute>
            <LexiStarTripsDrill />
          </ProtectedRoute>
        }
      />

      {/* LexiStar control tower (ST-00188 M2-U) — permissions in config/pagePermissions */}
      <Route
        path="/lexistar/status-board"
        element={
          <ProtectedRoute>
            <LexiStarStatusBoard />
          </ProtectedRoute>
        }
      />
      {/* LexiStar maintenance (ST-00249) — lexistar.maintenance.view | .manage in config/pagePermissions */}
      <Route
        path="/lexistar/maintenance"
        element={
          <ProtectedRoute>
            <LexiStarMaintenance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/approvals"
        element={
          <ProtectedRoute>
            <LexiStarApprovals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/trips/:id"
        element={
          <ProtectedRoute>
            <LexiStarTripDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/plan"
        element={
          <ProtectedRoute>
            <LexiStarPlanDispatch />
          </ProtectedRoute>
        }
      />
      {/* LexiStar dispatch planning board (ST-00250) — lexistar.trips.view in config/pagePermissions; changing needs lexistar.trips.manage (in-page) */}
      <Route
        path="/lexistar/dispatch-board"
        element={
          <ProtectedRoute>
            <LexiStarDispatchBoard />
          </ProtectedRoute>
        }
      />

      {/* LexiStar assurance (ST-00188 M3-U1) — permissions in config/pagePermissions */}
      <Route
        path="/lexistar/review"
        element={
          <ProtectedRoute>
            <LexiStarReviewQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/recompute-jobs"
        element={
          <ProtectedRoute>
            <LexiStarRecomputeJobs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/recompute-jobs/:id"
        element={
          <ProtectedRoute>
            <LexiStarRecomputeJobs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/commissioning"
        element={
          <ProtectedRoute>
            <LexiStarCommissioning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/commissioning/:id"
        element={
          <ProtectedRoute>
            <LexiStarCommissioning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/commissioning/:id/certificate"
        element={
          <ProtectedRoute>
            <LexiStarCertificate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/declarations"
        element={
          <ProtectedRoute>
            <LexiStarDeclarations />
          </ProtectedRoute>
        }
      />

      {/* LexiStar operations (ST-00188 M3-U2) — permissions in config/pagePermissions */}
      <Route
        path="/lexistar/plan-board"
        element={
          <ProtectedRoute>
            <LexiStarPlanBoard />
          </ProtectedRoute>
        }
      />
      {/* Replay | Trips | Idling heatmap (ST-00318): lexistar.replay.view or geofencing.view opens
          it; the page shows the tabs the viewer may use. */}
      <Route
        path="/lexistar/replay"
        element={
          <ProtectedRoute>
            <LexiStarReplay />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/drivers"
        element={
          <ProtectedRoute>
            <LexiStarDrivers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/messages"
        element={
          <ProtectedRoute>
            <LexiStarMessages />
          </ProtectedRoute>
        }
      />
      {/* ST-00251: the user's own notifications, filterable — the bell's page permission. */}
      <Route
        path="/lexistar/notifications"
        element={
          <ProtectedRoute>
            <LexiStarNotificationsInbox />
          </ProtectedRoute>
        }
      />

      {/* LexiStar reports (ST-00188 M4-R2) — lexistar.reports.view in config/pagePermissions;
          ?print=1 gives the A4 layout the renderer prints. A report opened with a one-time
          report_session token renders in the chrome-free ReportRenderShell (index.tsx). */}
      <Route
        path="/lexistar/reports"
        element={
          <ProtectedRoute>
            <LexiStarReportsCentre />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/reports/trip/:trip_id"
        element={
          <ProtectedRoute>
            <LexiStarTripReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/reports/run/:run_id"
        element={
          <ProtectedRoute>
            <LexiStarRunReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/reports/daily"
        element={
          <ProtectedRoute>
            <LexiStarDailySummary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/reports/daily/:date"
        element={
          <ProtectedRoute>
            <LexiStarDailySummary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/reports/:key"
        element={
          <ProtectedRoute>
            <LexiStarStandardReport />
          </ProtectedRoute>
        }
      />

      {/* LexiStar usage & billing (ST-00207 M4b-A) — lexistar.billing.view in config/pagePermissions.
          /statement/:month is the page report definition usage_statement prints (?print=1&run=). */}
      <Route
        path="/lexistar/billing"
        element={
          <ProtectedRoute>
            <LexiStarBilling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lexistar/billing/statement/:month"
        element={
          <ProtectedRoute>
            <LexiStarStatementPrint />
          </ProtectedRoute>
        }
      />

      <Route path="/driver" element={<DriverPWA />} />

      <Route
        path="/edge-sync"
        element={
          <ProtectedRoute>
            <SyncDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/telegram-bi"
        element={
          <ProtectedRoute>
            <TelegramDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/telegram-bi/queries"
        element={
          <ProtectedRoute>
            <TelegramQueryBuilder />
          </ProtectedRoute>
        }
      />

      <Route
        path="/telegram-bi/templates"
        element={
          <ProtectedRoute>
            <TelegramTemplateManager />
          </ProtectedRoute>
        }
      />

      <Route
        path="/telegram-bi/schedules"
        element={
          <ProtectedRoute>
            <TelegramScheduleManager />
          </ProtectedRoute>
        }
      />

      <Route
        path="/telegram-bi/analytics"
        element={
          <ProtectedRoute>
            <TelegramAnalytics />
          </ProtectedRoute>
        }
      />

      <Route
        path="/telegram-bi/bot"
        element={
          <ProtectedRoute>
            <TelegramBotManager />
          </ProtectedRoute>
        }
      />

      <Route
        path="/weighbridge-compliance"
        element={
          <ProtectedRoute>
            <WeighbridgeCompliance />
          </ProtectedRoute>
        }
      />

      {/* Help & training (ST-00200): landing, training track, glossary, a section, a guide.
          The static /help/training and /help/glossary routes win over /help/:section. */}
      <Route
        path="/help"
        element={
          <LazyPage>
            <HelpHome />
          </LazyPage>
        }
      />
      <Route
        path="/help/training"
        element={
          <LazyPage>
            <HelpTraining />
          </LazyPage>
        }
      />
      <Route
        path="/help/glossary"
        element={
          <LazyPage>
            <HelpGlossary />
          </LazyPage>
        }
      />
      <Route
        path="/help/:section"
        element={
          <LazyPage>
            <HelpSectionPage />
          </LazyPage>
        }
      />
      <Route
        path="/help/:section/:slug"
        element={
          <LazyPage>
            <HelpArticlePage />
          </LazyPage>
        }
      />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}
